import React, { useState, useContext, useMemo } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { PredContext } from './sportsc/predContext';

// import { PredContext } from './path-to-context'; // Ensure PredContext is imported

// --- LINES VIEW STYLED COMPONENTS ---

const ActionRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e2e8f0;
`;

const ActionButton = styled.button`
  padding: 8px 14px;
  font-size: 0.825rem;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  border: none;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.85;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ClearButton = styled(ActionButton)`
  background-color: #ef4444;
  color: white;
`;

const SendButton = styled(ActionButton)`
  background-color: #16a34a;
  color: white;
`;

const SecondaryButton = styled(ActionButton)`
  background-color: #64748b;
  color: white;
`;

const LinesContainer = styled.div`
  background: white;
  margin: 0 1rem 1rem;
  padding: 1rem;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 450px;
  overflow-y: auto;
`;

const LinesTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  text-align: left;
  font-size: 0.875rem;

  th {
    padding: 8px;
    color: #64748b;
    font-size: 0.75rem;
    text-transform: uppercase;
    border-bottom: 2px solid #e2e8f0;
    position: sticky;
    top: 0;
    background: white;
  }

  td {
    padding: 10px 8px;
    border-bottom: 1px solid #f1f5f9;
    color: #0f172a;
    font-weight: 500;
  }

  tr:last-child td {
    border-bottom: none;
  }
`;

const Badge = styled.span`
  background: #e2e8f0;
  color: #0e141b;
  font-weight: 700;
  font-size: 0.75rem;
  padding: 4px 8px;
  border-radius: 6px;
  text-transform: uppercase;
`;

const PickBadge = styled(Badge)`
  background: ${(props) => (props.$pick === 'OVER' ? '#dcfce7' : '#fee2e2')};
  color: ${(props) => (props.$pick === 'OVER' ? '#15803d' : '#b91c1c')};
`;

const EmptyText = styled.p`
  color: #64748b;
  font-size: 0.875rem;
  text-align: center;
  margin: 1rem 0;
`;

// --- LINES VIEW COMPONENT ---

function LinesView({ onSendLines }) {
  const { lines, clearLines } = useContext(PredContext);

  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Flatten the lines object into a readable array
  const formattedLines = useMemo(() => {
    if (!lines) return [];

    const result = [];
    Object.entries(lines).forEach(([statType, playersMap]) => {
      if (!playersMap) return;
      Object.entries(playersMap).forEach(([player, data]) => {
        result.push({
          player,
          type: statType,
          line: data.line,
          team: data.team ? data.team.replace(/\.csv$/, '') : 'N/A',
        });
      });
    });

    return result;
  }, [lines]);

  const fetchLines = async (payload) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios({
        method: 'post',
        url: `https://sx7vpiy3ij.execute-api.us-east-2.amazonaws.com/admin_dev/team-pull`,
        withCredentials: false,
        data: {
          option: 'lines',
          players: payload,
        },
      });
      // Set returned prediction matrix data
      setResults(res.data?.data || []);
    } catch (err) {
      console.error('Error fetching lines:', err);
      setError('Failed to fetch predictions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSend = () => {
    if (onSendLines) {
      onSendLines(formattedLines);
    } else {
      fetchLines(formattedLines);
    }
  };

  const handleClearAll = () => {
    setResults(null);
    clearLines();
  };

  // 1. Loading State
  if (loading) {
    return (
      <LinesContainer>
        <EmptyText>Fetching predictions...</EmptyText>
      </LinesContainer>
    );
  }

  // 2. Render Results View (When API data is available)
  if (results) {
    return (
      <LinesContainer>
        {error && <EmptyText style={{ color: '#ef4444' }}>{error}</EmptyText>}
        <LinesTable>
          <thead>
            <tr>
              <th>Player</th>
              <th>Metric</th>
              <th>Model</th>
              <th>Pred</th>
              <th>Line</th>
              <th>Edge</th>
              <th>Pick</th>
              <th>Win %</th>
            </tr>
          </thead>
          <tbody>
            {results.map((row, idx) => {
              const [
                player,
                metric,
                model,
                prediction,
                line,
                edge,
                rmse,
                pick,
                winProb,
              ] = row;

              return (
                <tr key={`${player}-${model}-${idx}`}>
                  <td style={{ fontWeight: 600 }}>{player}</td>
                  <td><Badge>{metric}</Badge></td>
                  <td style={{ textTransform: 'capitalize' }}>{model}</td>
                  <td>{Number(prediction).toFixed(2)}</td>
                  <td style={{ fontWeight: 700 }}>{line}</td>
                  <td style={{ color: edge > 0 ? '#16a34a' : '#dc2626', fontWeight: 600 }}>
                    {edge > 0 ? `+${edge}` : edge}
                  </td>
                  <td>
                    <PickBadge $pick={pick}>{pick}</PickBadge>
                  </td>
                  <td style={{ fontWeight: 700 }}>
                    {(winProb * 100).toFixed(1)}%
                  </td>
                </tr>
              );
            })}
          </tbody>
        </LinesTable>

        <ActionRow>
          <SecondaryButton onClick={() => setResults(null)}>Back to Input</SecondaryButton>
          <ClearButton onClick={handleClearAll}>Clear All</ClearButton>
        </ActionRow>
      </LinesContainer>
    );
  }

  // 3. Render Empty State
  if (formattedLines.length === 0) {
    return (
      <LinesContainer>
        <EmptyText>No active lines submitted yet.</EmptyText>
      </LinesContainer>
    );
  }

  // 4. Default Input Lines View
  return (
    <LinesContainer>
      {error && <EmptyText style={{ color: '#ef4444' }}>{error}</EmptyText>}
      <LinesTable>
        <thead>
          <tr>
            <th>Player</th>
            <th>Type</th>
            <th>Line</th>
            <th>Team</th>
          </tr>
        </thead>
        <tbody>
          {formattedLines.map((item, idx) => (
            <tr key={`${item.player}-${item.type}-${idx}`}>
              <td style={{ fontWeight: 600 }}>{item.player}</td>
              <td><Badge>{item.type}</Badge></td>
              <td style={{ fontWeight: 700, color: '#16a34a' }}>{item.line}</td>
              <td>{item.team}</td>
            </tr>
          ))}
        </tbody>
      </LinesTable>

      <ActionRow>
        <ClearButton onClick={clearLines}>Clear Lines</ClearButton>
        <SendButton onClick={handleSend}>Send Lines</SendButton>
      </ActionRow>
    </LinesContainer>
  );
}

export default LinesView;